import { v4 as uuid } from 'uuid';

import { validatePayload } from '@luxuryescapes/contract-svc-promo';

import { PROMO_AI_INSTRUCTIONS, VOUCHER_AI_INSTRUCTIONS } from '~/components/ContentTools/promoVoucherAiInstructions';

import {
  type CreateOrUpdatePromoPayload,
  type CreateVoucherPayload,
  type PreviewVoucherPayload,
  type UpdateVoucherPayload,
  type Voucher,
  checkPromoDraft,
  checkPromoRequest,
  checkVoucherCode,
  createOrUpdatePromo,
  createVoucher,
  getInternalPromo,
  getPromoMeta,
  getPromoVoucherWriteSchema,
  getVoucher,
  listVouchers,
  updateVoucher,
} from '~/services/PromoService';

import { createOpenAIClient } from '~/utils/openaiClient';

export const VOUCHER_VALIDATION_MAX_ATTEMPTS = 3;

export type AiCodeKind = 'promo' | 'voucher';
export type AiCodeOperation = 'create' | 'update';

/** Generate an identity before AI preparation so follow-up requests reuse the same code. */
export function generateAiCodeName(kind: AiCodeKind): string {
  return `${kind.toUpperCase()}-${uuid().replace(/-/g, '').toUpperCase()}`;
}
export type AiCodeProgressEvent = {
  step: 'identity' | 'metadata' | 'generation' | 'payload' | 'validation' | 'write' | 'readback' | 'check';
  status: 'running' | 'done' | 'failed' | 'skipped';
  detail: string;
  attempt?: number;
  errors?: Array<string>;
  warnings?: Array<string>;
};
export type AiCodeProgress = (event: AiCodeProgressEvent) => void;
export type AiCodeMessage = { role: 'user' | 'assistant'; content: string };
export type AiCodeContext = {
  kind: AiCodeKind;
  operation: AiCodeOperation;
  codeName: string;
  brand: CreateVoucherPayload['brand'];
  request: string;
  conversation?: Array<AiCodeMessage>;
  previousProposal?: unknown;
  manualEdits?: Record<string, unknown>;
  formDraft?: Record<string, unknown>;
};
type ExistingCode = { id: string; version: string; record: Record<string, unknown> };
export type AiCodeProposal = {
  type: 'proposal';
  context: AiCodeContext;
  summary: string;
  payload: CreateOrUpdatePromoPayload | CreateVoucherPayload | UpdateVoucherPayload;
  existing: ExistingCode | null;
  preview: unknown;
  warnings: Array<string>;
  errors: Array<string>;
  validationAttempts: number;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
};
export type AiCodeQuestion = {
  type: 'question';
  context: AiCodeContext;
  summary: string;
  questions: Array<string>;
};
export type AiCodeGenerationResult = AiCodeProposal | AiCodeQuestion;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

function promoVersion(record: Record<string, unknown>): string {
  return typeof record.updated_at === 'string' ? record.updated_at : '';
}

function voucherVersion(record: Record<string, unknown>): string {
  return typeof record.updatedAt === 'string' ? record.updatedAt : '';
}

export async function findExistingCode(
  kind: AiCodeKind,
  codeName: string,
  brand: CreateVoucherPayload['brand'],
): Promise<ExistingCode | null> {
  if (kind === 'promo') {
    try {
      const response = await getInternalPromo({ code_name: codeName, brand });
      const record = response.result as Record<string, unknown>;
      if (record.code_name !== codeName || record.brand !== brand || typeof record.id_promo_code !== 'string') {
        throw new Error('Promo identity did not match the requested code and brand.');
      }
      return { id: record.id_promo_code, version: promoVersion(record), record };
    } catch (error) {
      if (isObject(error) && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  const response = await listVouchers({ voucher_name: codeName, brand, only_active: false, page: 0, limit: 100 });
  const matches = response.result.vouchers.filter(
    (voucher) => voucher.voucherName === codeName && voucher.brand === brand,
  );
  if (matches.length > 1) {
    throw new Error('Multiple vouchers matched this name and brand.');
  }
  if (!matches[0]) {
    return null;
  }
  const detail = await getVoucher(matches[0].voucherId);
  const record = detail.result as unknown as Record<string, unknown>;
  if (record.voucherName !== codeName || record.brand !== brand) {
    throw new Error('Voucher identity did not match the requested code and brand.');
  }
  return { id: matches[0].voucherId, version: voucherVersion(record), record };
}

function voucherPreviewFromRecord(voucher: Voucher): PreviewVoucherPayload {
  const compatibility = voucher as Voucher & {
    voucher_rewards?: Voucher['voucherRewards'];
    voucher_conditions?: Voucher['voucherConditions'];
  };
  return {
    voucher_name: voucher.voucherName,
    brand: voucher.brand,
    voucher_title: voucher.voucherTitle,
    voucher_description: voucher.voucherDescription,
    voucher_call_to_action: voucher.voucherCallToAction,
    voucher_call_to_action_url: voucher.voucherCallToActionUrl,
    voucher_presentation_config: voucher.voucherPresentationConfig,
    voucher_breakdown_title: voucher.voucherBreakdownTitle,
    voucher_amount_display_override: voucher.voucherAmountDisplayOverride,
    voucher_conditions: voucher.voucherConditions ?? compatibility.voucher_conditions ?? [],
    voucher_rewards: voucher.voucherRewards ?? compatibility.voucher_rewards ?? [],
    voucher_toggles: voucher.voucherToggles,
    voucher_reporting_tags: voucher.voucherReportingTags,
    show_voucher_from_date: voucher.showVoucherFromDate,
    voucher_user_expires_at: voucher.voucherUserExpiresAt,
    voucher_user_expires_in_days: voucher.voucherUserExpiresInDays,
    theme: voucher.theme,
    product_type: voucher.productType,
    case_id: voucher.caseId,
    reason: voucher.reason,
    reward_kind: voucher.rewardKind,
  };
}

export function buildVoucherPreview(
  payload: CreateVoucherPayload | UpdateVoucherPayload,
  existing: ExistingCode | null,
): PreviewVoucherPayload {
  if (!existing) {
    return payload as CreateVoucherPayload;
  }
  const base = voucherPreviewFromRecord(existing.record as Voucher);
  const previewUpdates = Object.fromEntries(Object.entries(payload).filter(([field]) => field in base));
  return { ...base, ...previewUpdates };
}

function changesForPayload(
  context: AiCodeContext,
  payload: CreateOrUpdatePromoPayload | CreateVoucherPayload | UpdateVoucherPayload,
  existing: ExistingCode | null,
): AiCodeProposal['changes'] {
  const current = existing
    ? context.kind === 'voucher'
      ? (voucherPreviewFromRecord(existing.record as Voucher) as Record<string, unknown>)
      : existing.record
    : {};
  const candidate = context.kind === 'promo' ? (payload as CreateOrUpdatePromoPayload) : payload;
  const entries =
    context.kind === 'promo'
      ? [
          ...Object.entries((candidate as CreateOrUpdatePromoPayload).promo).map(([field, after]) => ({
            field: `promo.${field}`,
            before: current[field],
            after,
          })),
          ...(['promoDiscounts', 'promoConditions'] as const)
            .filter((field) => field in candidate)
            .map((field) => ({
              field,
              before: current[field === 'promoDiscounts' ? 'promo_discounts' : 'promo_conditions'],
              after: candidate[field],
            })),
          ...(['promoDiscount', 'promoCondition'] as const)
            .filter((field) => field in candidate)
            .map((field) => ({
              field,
              before: current[field === 'promoDiscount' ? 'promo_discounts' : 'promo_conditions'],
              after: candidate[field],
            })),
        ]
      : Object.entries(candidate).map(([field, after]) => ({ field, before: current[field], after }));
  return entries.filter(({ before, after }) => JSON.stringify(before) !== JSON.stringify(after));
}

function checkVoucherRowIds(
  rows: Array<Record<string, unknown>>,
  existingRows: Array<Record<string, unknown>>,
  idField: 'id_voucher_reward' | 'id_voucher_condition',
): void {
  const existingIds = new Set(existingRows.map((row) => row[idField]).filter((id): id is string => Boolean(id)));
  const proposedIds = new Set(rows.map((row) => row[idField]).filter((id): id is string => Boolean(id)));
  if (rows.some((row) => !row[idField]) && [...existingIds].some((id) => !proposedIds.has(id))) {
    throw new Error(
      `A voucher row is missing ${idField} while an existing row is absent. Preserve existing IDs and omit IDs only for new rows.`,
    );
  }
  if ([...proposedIds].some((id) => !existingIds.has(id))) {
    throw new Error(`Unknown ${idField}. Preserve existing IDs and omit IDs for new rows.`);
  }
}

export function normalizeAiPayload(
  context: AiCodeContext,
  value: unknown,
  existing: ExistingCode | null,
): CreateOrUpdatePromoPayload | CreateVoucherPayload | UpdateVoucherPayload {
  if (!isObject(value) || Object.keys(value).length === 0) {
    throw new Error('The AI did not return a write payload.');
  }
  if (context.kind === 'promo') {
    if (!isObject(value.promo)) {
      throw new Error('Promo proposal is missing its promo object.');
    }
    if (value.promo.code_name && value.promo.code_name !== context.codeName) {
      throw new Error('Promo name changed.');
    }
    if (value.promo.brand && value.promo.brand !== context.brand) {
      throw new Error('Promo brand changed.');
    }
    if (context.operation === 'create' && (value.promo.id_promo_code || value.mode === 'update-only')) {
      throw new Error('Create proposal contains an existing promo ID or update mode.');
    }
    if (context.operation === 'update' && !existing) {
      throw new Error('Promo no longer exists.');
    }
    if (context.operation === 'update' && value.promo.id_promo_code && value.promo.id_promo_code !== existing?.id) {
      throw new Error('Promo ID changed.');
    }
    return {
      ...value,
      requestSource: 'admin-promo-ai',
      mode: context.operation === 'create' ? 'create-only' : 'update-only',
      promo: {
        ...value.promo,
        code_name: context.codeName,
        brand: context.brand,
        ...(existing ? { id_promo_code: existing.id } : {}),
      },
    };
  }
  if (value.voucher_name && value.voucher_name !== context.codeName) {
    throw new Error('Voucher name changed.');
  }
  if (value.brand && value.brand !== context.brand) {
    throw new Error('Voucher brand changed.');
  }
  if (value.user_id || value.voucher_id) {
    throw new Error('Voucher assignment or ID fields are outside this workflow.');
  }
  if (context.operation === 'create') {
    return {
      ...value,
      voucher_name: context.codeName,
      brand: context.brand,
      ...(Array.isArray(value.voucher_rewards)
        ? {
            voucher_rewards: value.voucher_rewards.map((reward) => ({
              ...reward,
              id_voucher_reward: reward.id_voucher_reward || uuid(),
            })),
          }
        : {}),
    };
  }
  if (!existing) {
    throw new Error('Voucher no longer exists.');
  }
  const { voucher_name: _name, brand: _brand, ...update } = value;
  const current = voucherPreviewFromRecord(existing.record as Voucher);
  if (Array.isArray(update.voucher_rewards)) {
    checkVoucherRowIds(update.voucher_rewards, current.voucher_rewards, 'id_voucher_reward');
  }
  if (Array.isArray(update.voucher_conditions)) {
    checkVoucherRowIds(update.voucher_conditions, current.voucher_conditions, 'id_voucher_condition');
  }
  return {
    ...update,
    ...(Array.isArray(update.voucher_rewards)
      ? {
          voucher_rewards: update.voucher_rewards.map((reward) => ({
            ...reward,
            id_voucher_reward: reward.id_voucher_reward || uuid(),
          })),
        }
      : {}),
  };
}

export async function prepareAiCodeProposal(
  context: AiCodeContext,
  onProgress?: AiCodeProgress,
): Promise<AiCodeGenerationResult> {
  if (!context.codeName.trim()) {
    if (context.operation !== 'create') {
      throw new Error('Enter a code name to update an existing promo or voucher.');
    }
    context = { ...context, codeName: generateAiCodeName(context.kind) };
  }
  onProgress?.({
    step: 'identity',
    status: 'running',
    detail: `Checking ${context.brand} ${context.kind} ${context.codeName}`,
  });
  const existing = await findExistingCode(context.kind, context.codeName, context.brand);
  if (context.operation === 'create' && existing) {
    throw new Error('This code already exists for the selected brand.');
  }
  if (context.operation === 'update' && !existing) {
    throw new Error('No existing code matched this name and brand.');
  }
  onProgress?.({ step: 'identity', status: 'done', detail: existing ? 'Existing code found' : 'Name is available' });
  onProgress?.({ step: 'metadata', status: 'running', detail: 'Loading Promo Meta' });
  const metadata = await getPromoMeta();
  const writePayloadSchema = await getPromoVoucherWriteSchema(context.kind, existing?.id);
  const meta = metadata.result;
  const relevantMeta =
    context.kind === 'promo'
      ? {
          promo_types: meta.promo_types,
          promo_toggles: meta.promo_toggles,
          promo_region_groupings: meta.promo_region_groupings,
          promo_product_types: meta.promo_product_types,
          promo_condition_types: meta.promo_condition_types,
          promo_tags: meta.promo_tags,
        }
      : {
          voucher_toggles: meta.voucher_toggles,
          voucher_condition_types: meta.voucher_condition_types,
          voucher_product_types: meta.voucher_product_types,
          voucher_themes: meta.voucher_themes,
          voucher_reward_kind_options: meta.voucher_reward_kind_options,
        };
  const payloadShape =
    context.kind === 'promo'
      ? 'Use a promo object for promo fields, with promoDiscounts and promoConditions only when needed.'
      : context.operation === 'create'
        ? 'Use voucher_title, voucher_description, voucher_rewards, voucher_toggles, and any other required voucher fields from Promo Meta.'
        : 'Use only changed voucher fields such as voucher_title, voucher_rewards, or voucher_conditions.';
  const instructions = context.kind === 'promo' ? PROMO_AI_INSTRUCTIONS : VOUCHER_AI_INSTRUCTIONS;
  const identityFields = context.kind === 'promo' ? 'code name, brand, and promo mode' : 'voucher name and brand';
  onProgress?.({
    step: 'metadata',
    status: 'done',
    detail: `Loaded ${Object.keys(relevantMeta).length} metadata groups`,
  });
  const maxAttempts = context.kind === 'voucher' ? VOUCHER_VALIDATION_MAX_ATTEMPTS : 1;
  let validationFeedback: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    onProgress?.({
      step: 'generation',
      status: 'running',
      detail: `Calling OpenAI gpt-5.4 with ${context.operation} request, ${existing ? 'current record, ' : ''}${Object.keys(relevantMeta).length} metadata groups (attempt ${attempt}/${maxAttempts})`,
    });
    const response = await createOpenAIClient().responses.create({
      model: 'gpt-5.4',
      instructions,
      input: `Return a JSON response with a short summary, a payloadJson string containing the proposed write payload as JSON, and a questions array. ${payloadShape} If validationFeedback is present, correct the previous payload using its errors, warnings, and validation result. Preserve the requested intent; do not invent missing business requirements or remove restrictions merely to pass validation. Manual edits are deliberate operator choices: preserve their values and use the form draft as context. If a previous proposal is present, treat it as an unsaved draft and incorporate the operator's latest corrections. Use writePayloadSchema as the JSON Schema for the object encoded in payloadJson, including its nested fields, types, enums, and required fields. It describes the final endpoint body after application normalization; application-managed identity and new row IDs may be omitted. For updates, include only requested changes. The application fills in the ${identityFields}; include only the business fields needed for the request. If required details are missing, put specific questions in the questions array and set payloadJson to "{}". When returning a proposal, use an empty questions array.\n\nContext:\n${JSON.stringify(
        {
          kind: context.kind,
          operation: context.operation,
          codeName: context.codeName,
          brand: context.brand,
          request: context.request,
          conversation: context.conversation ?? [],
          previousProposal: context.previousProposal ?? null,
          currentRecord: existing?.record ?? null,
          promoMeta: relevantMeta,
          writePayloadSchema,
          manualEdits: context.manualEdits ?? null,
          formDraft: context.formDraft ?? null,
          validationFeedback,
        },
      )}`,
      text: {
        format: {
          type: 'json_schema',
          name: context.kind === 'promo' ? 'promo_proposal' : 'voucher_proposal',
          strict: true,
          schema: {
            type: 'object',
            properties: {
              summary: { type: 'string', description: 'Short explanation for the operator.' },
              payloadJson: {
                type: 'string',
                description: `A JSON object containing only the requested write endpoint fields. ${payloadShape}`,
              },
              questions: { type: 'array', items: { type: 'string' } },
            },
            required: ['summary', 'payloadJson', 'questions'],
            additionalProperties: false,
          },
        },
      },
    });
    onProgress?.({ step: 'generation', status: 'done', detail: 'OpenAI response received' });
    onProgress?.({ step: 'payload', status: 'running', detail: 'Parsing and checking the proposed write payload' });
    let output: unknown;
    try {
      output = JSON.parse(response.output_text);
    } catch (error) {
      throw new Error('The AI response was not valid JSON. Try generating again.', { cause: error });
    }
    if (!isObject(output)) {
      throw new Error('The AI response was not a proposal.');
    }
    if (typeof output.payloadJson !== 'string') {
      throw new Error('The AI response was missing its payload JSON.');
    }
    if (!Array.isArray(output.questions) || !output.questions.every((question) => typeof question === 'string')) {
      throw new Error('The AI response was missing its questions array.');
    }
    const questions = output.questions.map((question: string) => question.trim()).filter(Boolean);
    let payloadJson: unknown;
    try {
      payloadJson = JSON.parse(output.payloadJson);
    } catch (error) {
      throw new Error('The AI payload was not valid JSON. Try generating again.', { cause: error });
    }
    if (questions.length > 0) {
      if (!isObject(payloadJson) || Object.keys(payloadJson).length > 0) {
        throw new Error('The AI returned questions and a write payload together. Generate again.');
      }
      onProgress?.({ step: 'payload', status: 'done', detail: 'AI needs more information before preparing a payload' });
      return {
        type: 'question',
        context,
        summary: typeof output.summary === 'string' ? output.summary : 'More details are needed.',
        questions,
      };
    }
    if (isObject(payloadJson) && Object.keys(payloadJson).length === 0) {
      throw new Error(
        typeof output.summary === 'string' && output.summary.trim()
          ? output.summary
          : 'The AI needs more details to prepare a proposal.',
      );
    }
    const payload = normalizeAiPayload(context, payloadJson, existing);
    onProgress?.({
      step: 'payload',
      status: 'done',
      detail: `Accepted ${Object.keys(payload).length} top-level payload fields`,
    });
    const summary = typeof output.summary === 'string' ? output.summary : 'Review the proposed changes.';
    let preview: unknown = null;
    let warnings: Array<string> = [];
    let errors: Array<string> = [];
    if (context.kind === 'promo' && context.operation === 'create') {
      onProgress?.({ step: 'validation', status: 'running', detail: 'Checking the promo draft with the server' });
      const check = await checkPromoDraft(payload as CreateOrUpdatePromoPayload);
      preview = check.result;
      warnings = check.result.promo_warnings
        .filter((warning) => warning.level !== 'error')
        .map((warning) => warning.message ?? warning.code);
      errors = check.result.promo_warnings
        .filter((warning) => warning.level === 'error')
        .map((warning) => warning.message ?? warning.code);
    } else if (context.kind === 'voucher') {
      onProgress?.({ step: 'validation', status: 'running', detail: 'Validating the voucher write payload' });
      const options = {
        existingVoucherName: context.codeName,
        existingRewardIds: existing
          ? voucherPreviewFromRecord(existing.record as Voucher).voucher_rewards.map(
              (reward) => reward.id_voucher_reward,
            )
          : [],
        voucherNameExists: async (voucherName: string, brand: string) => {
          if (brand !== context.brand) {
            throw new Error('Voucher brand changed.');
          }
          const match = await findExistingCode('voucher', voucherName, context.brand);
          return match !== null;
        },
      };
      // The contract's public dispatcher calls validateVoucherPayload for voucher inputs.
      const validation = await validatePayload(
        context.operation === 'create'
          ? { type: 'voucher', operation: 'create', payload: payload as CreateVoucherPayload, options }
          : { type: 'voucher', operation: 'update', payload: payload as UpdateVoucherPayload, options },
      );
      preview = validation;
      warnings = validation.validationResults
        .filter((result) => !result.isValid && result.level !== 'error')
        .map((result) => `${result.check}: ${result.fixSuggestion}`);
      errors = validation.validationResults
        .filter((result) => !result.isValid && result.level === 'error')
        .map((result) => `${result.check}: ${result.fixSuggestion}`);
    } else {
      onProgress?.({
        step: 'validation',
        status: 'skipped',
        detail: 'No pre-save draft check is available for promo updates',
      });
    }
    if (context.kind !== 'promo' || context.operation === 'create') {
      onProgress?.({
        step: 'validation',
        status: warnings.length > 0 || errors.length > 0 ? 'failed' : 'done',
        detail: `Validation returned ${warnings.length} warnings and ${errors.length} errors`,
        attempt,
        errors,
        warnings,
      });
    }
    if (context.kind === 'voucher' && (warnings.length > 0 || errors.length > 0) && attempt < maxAttempts) {
      validationFeedback = { previousPayload: payload, warnings, errors, validationResult: preview };
      continue;
    }
    return {
      type: 'proposal',
      context,
      validationAttempts: attempt,
      summary,
      payload,
      existing,
      preview,
      warnings,
      errors,
      changes: changesForPayload(context, payload, existing),
    };
  }
  throw new Error('No proposal was generated.');
}

export async function saveAiCodeProposal(
  proposal: AiCodeProposal,
  onProgress?: AiCodeProgress,
): Promise<{ saved: unknown; check: unknown; mismatches: Array<string> }> {
  const { context, payload, existing } = proposal;
  if (context.kind === 'voucher' && (proposal.errors.length > 0 || proposal.warnings.length > 0)) {
    throw new Error('Voucher validation still has errors or warnings. Clarify the request and prepare a new proposal.');
  }
  if (proposal.errors.length > 0) {
    throw new Error('The pre-save check reported errors. Generate a new proposal.');
  }
  onProgress?.({ step: 'identity', status: 'running', detail: 'Checking the code has not changed during review' });
  const latest = await findExistingCode(context.kind, context.codeName, context.brand);
  if (context.operation === 'create' && latest) {
    throw new Error('Code name is now taken. Generate a new proposal.');
  }
  if (context.operation === 'update' && (latest?.id !== existing?.id || latest.version !== existing.version)) {
    throw new Error('The saved code changed during review. Generate a new proposal.');
  }
  onProgress?.({ step: 'identity', status: 'done', detail: 'Code identity is unchanged' });
  onProgress?.({
    step: 'write',
    status: 'running',
    detail: `${context.operation === 'create' ? 'Creating' : 'Updating'} ${context.kind}`,
  });
  let saved: unknown = null;
  let savedVoucherId = existing?.id;
  if (context.kind === 'promo') {
    await createOrUpdatePromo(payload as CreateOrUpdatePromoPayload);
  } else if (context.operation === 'create') {
    const created = await createVoucher({
      ...(payload as CreateVoucherPayload),
      requestSource: 'admin-voucher-ai',
    });
    savedVoucherId = created.result.voucherId;
  } else {
    await updateVoucher(existing.id, {
      ...(payload as UpdateVoucherPayload),
      requestSource: 'admin-voucher-ai',
    });
  }
  onProgress?.({ step: 'write', status: 'done', detail: 'Write completed' });
  const mismatches: Array<string> = [];
  onProgress?.({ step: 'readback', status: 'running', detail: 'Reading the saved code back' });
  try {
    saved =
      context.kind === 'promo'
        ? (await getInternalPromo({ code_name: context.codeName, brand: context.brand })).result
        : savedVoucherId
          ? (await getVoucher(savedVoucherId)).result
          : null;
    if (!saved) {
      mismatches.push('Readback unavailable: missing saved voucher ID');
    }
  } catch (error) {
    mismatches.push(`Readback unavailable: ${formatError(error)}`);
  }
  if (saved) {
    const savedRecord = saved as Record<string, unknown>;
    const mappedVoucher =
      context.kind === 'voucher' ? (voucherPreviewFromRecord(saved as Voucher) as Record<string, unknown>) : null;
    mismatches.push(
      ...proposal.changes
        .filter(({ field, after }) => {
          const actual =
            context.kind === 'promo'
              ? field.startsWith('promo.')
                ? savedRecord[field.slice(6)]
                : savedRecord[field === 'promoDiscounts' ? 'promo_discounts' : 'promo_conditions']
              : mappedVoucher?.[field];
          return !matchesExpected(after, actual);
        })
        .map(({ field }) => field),
    );
  }
  onProgress?.({
    step: 'readback',
    status: mismatches.length === 0 ? 'done' : 'failed',
    detail: mismatches.length === 0 ? 'Saved code matches the reviewed changes' : 'Readback differs or is unavailable',
  });
  let check: unknown;
  onProgress?.({ step: 'check', status: 'running', detail: 'Checking the saved code with the server' });
  try {
    check =
      context.kind === 'promo'
        ? await checkPromoRequest({ codeName: context.codeName, brand: context.brand })
        : await checkVoucherCode({ voucherName: context.codeName, brand: context.brand });
  } catch (error) {
    check = { error: formatError(error) };
    mismatches.push('Code check failed');
  }
  const checkResult = isObject(check) && isObject(check.result) ? check.result : null;
  if (context.kind === 'promo' && checkResult?.status === 'non-existent') {
    mismatches.push('Code check did not find the saved promo');
  }
  onProgress?.({
    step: 'check',
    status: mismatches.some((mismatch) => mismatch.startsWith('Code check')) ? 'failed' : 'done',
    detail: `Code check finished; ${mismatches.length} findings`,
  });
  return { saved, check, mismatches };
}

function matchesExpected(expected: unknown, actual: unknown): boolean {
  if (Array.isArray(expected)) {
    return Array.isArray(actual) && expected.every((item) => actual.some((row) => matchesExpected(item, row)));
  }
  if (isObject(expected)) {
    return isObject(actual) && Object.entries(expected).every(([key, value]) => matchesExpected(value, actual[key]));
  }
  return expected === actual;
}
